/**
 * ContentSourceEditor — IMAGE-only Content Item picker for the
 * MergeFieldMapper. Used when a mapping has sourceType='content'.
 *
 * v1 scope (audit-AI 2026-06-04 GREEN):
 *   - Filter to content_type='IMAGE' AND is_public=true at fetch time.
 *   - Typeahead (200ms debounce) calls getContentItems({search, content_type, is_public}).
 *   - On selection, the content item id is stored in mapping.sourceField.
 *   - Display "Content: <title>" summary when a value is selected.
 *   - Stale-reference handling: if mapping.sourceField is set but no
 *     content item resolves at load time (deleted / private / wrong
 *     type), show a row-level error.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import type { FieldMapping } from "../../services/templates.service";
import {
  getContentItems,
  getContentItem,
  contentResolutionErrorLabel,
  resolveContentUrlsForMappings,
  type ContentResolutionErrorReason,
} from "../../services/content.service";
import type { ContentItem } from "../../types/content.types";

interface ContentSourceEditorProps {
  mapping: FieldMapping;
  onChange: (updates: Partial<FieldMapping>) => void;
}

const SEARCH_DEBOUNCE_MS = 200;
const RESULTS_LIMIT = 20;

export default function ContentSourceEditor({
  mapping,
  onChange,
}: ContentSourceEditorProps): React.ReactElement {
  const selectedId = mapping.sourceField || "";

  // Resolved item shown in the "Content: <title>" summary.
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [resolveError, setResolveError] =
    useState<ContentResolutionErrorReason | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<ContentItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Load the selected content item summary + validate it resolves.
  useEffect(() => {
    if (!selectedId) {
      setSelectedItem(null);
      setResolveError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const item = await getContentItem(selectedId);
        if (cancelled) {
          return;
        }
        setSelectedItem(item);
        const res = await resolveContentUrlsForMappings([selectedId]);
        if (cancelled) {
          return;
        }
        if (res.errors.length > 0) {
          setResolveError(res.errors[0].reason);
        } else {
          setResolveError(null);
        }
      } catch {
        if (cancelled) {
          return;
        }
        setSelectedItem(null);
        setResolveError("missing");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  // Debounced search for typeahead.
  useEffect(() => {
    if (!showResults) {
      return;
    }
    if (searchTerm.length === 0) {
      // Show recent IMAGE+public items
      let cancelled = false;
      (async () => {
        setSearching(true);
        try {
          const items = await getContentItems({
            content_type: ["IMAGE"],
            is_public: true,
          });
          if (!cancelled) {
            setSearchResults(items.slice(0, RESULTS_LIMIT));
          }
        } catch {
          if (!cancelled) {
            setSearchResults([]);
          }
        } finally {
          if (!cancelled) {
            setSearching(false);
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const items = await getContentItems({
          search: searchTerm,
          content_type: ["IMAGE"],
          is_public: true,
        });
        setSearchResults(items.slice(0, RESULTS_LIMIT));
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchTerm, showResults]);

  // Close results when clicking outside.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const errorMessage = useMemo(
    () => (resolveError ? contentResolutionErrorLabel(resolveError) : null),
    [resolveError],
  );

  return (
    <div className="mt-2" ref={containerRef}>
      {/* Current selection summary */}
      {selectedItem ? (
        <div
          className={`flex items-center justify-between px-3 py-2 rounded border ${
            resolveError
              ? "bg-red-50 border-red-200"
              : "bg-white border-gray-300"
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {selectedItem.thumbnail_url && (
              <img
                src={selectedItem.thumbnail_url}
                alt=""
                className="w-8 h-8 object-cover rounded flex-shrink-0"
              />
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">
                Content: {selectedItem.title}
              </div>
              <div className="text-xs text-gray-500">
                {selectedItem.content_type}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              onChange({ sourceField: "" });
              setSelectedItem(null);
              setSearchTerm("");
              setShowResults(true);
            }}
            className="text-xs text-primary-600 hover:text-primary-700 ml-2 flex-shrink-0"
          >
            Change
          </button>
        </div>
      ) : (
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setShowResults(true)}
            placeholder="Search Content Items (IMAGE only)..."
            className="w-full text-sm px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
          {showResults && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-64 overflow-y-auto">
              {searching ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  Searching...
                </div>
              ) : searchResults.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No IMAGE Content Items found
                </div>
              ) : (
                searchResults.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      onChange({ sourceField: item.id });
                      setShowResults(false);
                      setSearchTerm("");
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    {item.thumbnail_url && (
                      <img
                        src={item.thumbnail_url}
                        alt=""
                        className="w-8 h-8 object-cover rounded flex-shrink-0"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {item.title}
                      </div>
                      {item.description && (
                        <div className="text-xs text-gray-500 truncate">
                          {item.description}
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {item.content_type}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Stale-reference / validation error banner */}
      {errorMessage && (
        <div className="mt-1.5 px-2 py-1 rounded bg-red-50 border border-red-200 text-xs text-red-700">
          {errorMessage} — pick another Content Item.
        </div>
      )}

      {/* Empty-state helper when no mapping has been set yet */}
      {!selectedId && !errorMessage && (
        <p className="mt-1 text-xs text-amber-600">
          Pick an IMAGE Content Item or this field will be empty.
        </p>
      )}
    </div>
  );
}
